import { useEffect, useState, type ReactNode } from 'react';
import { Head, useAdmin } from '../ui/Shell';
import { Block, Cell, Field, Grid, Kv, Pill, Seg, Toggle, type KvRow } from '../ui/Block';
import { fmtDate, put } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { fallbackCell, useFallbackRate } from './machines';

interface ServicesConfig { openai: boolean; workos: boolean; apns: boolean; fcm: boolean; fly: boolean; github: boolean; translationProvider: string }

type Provider = 'auto' | 'google' | 'openai';
type Rates = Record<'engine' | 'api' | 'app', number>;
type Prices = Record<string, { input: number; output: number }>;
interface Stamp { updatedAt: string | null; updatedBy: string | null }
interface Setting extends Stamp {
  key: string;
  kind: 'bool' | 'number' | 'string' | 'json';
  default: unknown;
  description: string;
  options: string[];
  min: number | null;
  max: number | null;
  value: unknown;
}
const OWN_EDITOR = new Set(['translation_provider', 'cost_rates', 'llm_prices']);
interface SettingsReply {
  runtime: Partial<{ translation_provider: Stamp & { value: Provider }; cost_rates: Stamp & { value: Partial<Rates> }; llm_prices: Stamp & { value: Prices } }>;
  settings?: Setting[];
  deployTime: Record<string, string | string[] | null>;
  secrets: { name: string; present: boolean }[];
}

const APPS: (keyof Rates)[] = ['engine', 'api', 'app'];
const APP_LABEL: Record<keyof Rates, string> = { engine: 'Engine', api: 'API app', app: 'Web app' };
const TEST_SECRETS = new Set(['AUTH_DEV_SECRET', 'RUN_SECRET']);
const DEPLOY_NOTE: Record<string, string> = { ENGINE_CONCURRENCY: 'API app · one engine per transcription', DB_IDLE_AFTER_S: 'API app', PUBLISHER_GRACE_S: 'API app', MAX_PAUSE_S: 'API app', ENGINE_HOLD_S: 'API app', SUMMARY_MODEL: 'API app · the model the price table prices', WEBAPP_ORIGIN: 'API app · one origin per line', TRANSLATION_PROVIDER: 'API app · the default the runtime override replaces', PUBLIC_BASE_URL: 'API app', ENGINE_BASE_URL: 'API app · set only on a single-engine deployment' };

function Stamped({ s }: { s?: Stamp }) {
  if (!s?.updatedAt) return <span style={{ fontSize: 11, color: 'var(--ink-45)' }}>Default, never changed.</span>;
  return <span style={{ fontSize: 11, color: 'var(--ink-55)' }}>Changed {fmtDate(s.updatedAt)}{s.updatedBy ? ` by ${s.updatedBy}` : ''}</span>;
}

function Group({ title, sub, stamp, dirty, busy, onSave, children, saveLabel = 'Save' }: { title: ReactNode; sub?: ReactNode; stamp?: Stamp; dirty: boolean; busy: boolean; onSave: () => void; children: ReactNode; saveLabel?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px', borderBottom: '1px solid var(--hair)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
        {sub && <span style={{ fontSize: 11.5, fontWeight: 300, color: 'var(--ink-55)' }}>{sub}</span>}
        <span style={{ flex: 1 }} />
        <Stamped s={stamp} />
      </div>
      {children}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn--sm btn--primary" disabled={!dirty || busy} onClick={onSave}>{busy ? 'Saving…' : saveLabel}</button>
      </div>
    </div>
  );
}

const show = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v));

function Declared({ s, mobile, busy, onSave }: { s: Setting; mobile: boolean; busy: boolean; onSave: (v: unknown) => void }) {
  const [draft, setDraft] = useState<unknown>(s.value);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (!dirty) setDraft(s.value); }, [s.value, dirty]);
  const set = (v: unknown) => { if (mobile) return; setDraft(v); setDirty(true); };
  const n = Number(draft);
  const valid = s.kind !== 'number' || (String(draft).trim() !== '' && Number.isFinite(n) && (s.min == null || n >= s.min) && (s.max == null || n <= s.max));
  const value = s.kind === 'number' ? n : draft;
  const save = () => { onSave(value); setDirty(false); };
  return (
    <Group title={<span className="mono">{s.key}</span>} sub={`${s.kind} · default ${show(s.default)}`} stamp={s} dirty={dirty && valid && show(value) !== show(s.value)} busy={busy} onSave={save} saveLabel="Apply">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {s.kind === 'bool' && <Toggle on={draft === true} onChange={(v) => set(v)} label={<span className="mono" style={{ fontSize: 12 }}>{draft === true ? 'true' : 'false'}</span>} />}
        {s.kind === 'number' && <div style={{ width: 180 }}><Field label={s.min != null || s.max != null ? `${s.min ?? '…'} to ${s.max ?? '…'}` : 'Value'} type="number" mono value={String(draft ?? '')} onChange={(v) => set(v)} /></div>}
        {s.kind === 'string' && (s.options.length
          ? <Seg<string> value={String(draft)} options={s.options.map((o) => ({ value: o, label: o }))} onChange={(v) => set(v)} mono />
          : <div style={{ flex: 1, minWidth: 200 }}><Field label="Value" mono value={String(draft ?? '')} onChange={(v) => set(v)} /></div>)}
        <span style={{ flex: 1, minWidth: 220, fontSize: 12, fontWeight: 300, color: 'var(--ink-62)' }}>{s.description}</span>
      </div>
    </Group>
  );
}

export function Settings() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const d = useData<SettingsReply>(env, '/admin/settings', 30, paused);
  const ov = useData<{ config: ServicesConfig }>(env, '/admin/overview?wake=false', 30, paused);
  const fr = useFallbackRate(env, paused);
  const cfg = ov.data?.config;
  const set = (on?: boolean): KvRow['v'] => ({ pill: { label: on ? 'SET' : 'MISSING', tone: on ? 'ok' : 'bad' } });
  const s = d.data;
  const prod = env.key === 'production';

  const [provider, setProvider] = useState<Provider>('auto');
  const [rates, setRates] = useState<Record<keyof Rates, string>>({ engine: '', api: '', app: '' });
  const [prices, setPrices] = useState<{ model: string; input: string; output: string }[]>([]);
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    if (!s) return;
    if (!dirty.translation_provider) setProvider(s.runtime.translation_provider?.value ?? 'auto');
    if (!dirty.cost_rates) { const r = s.runtime.cost_rates?.value ?? {}; setRates({ engine: String(r.engine ?? ''), api: String(r.api ?? ''), app: String(r.app ?? '') }); }
    if (!dirty.llm_prices) setPrices(Object.entries(s.runtime.llm_prices?.value ?? {}).map(([model, p]) => ({ model, input: String(p.input), output: String(p.output) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s]);

  const save = async (key: string, value: unknown, text: string) => {
    setBusy(key);
    try { await put(env, `/admin/settings/${key}`, value); ctx.toast(text, 'Applied immediately'); setDirty((x) => ({ ...x, [key]: false })); d.reload(); } catch (e) { ctx.toast((e as Error).message, '', { logged: false }); } finally { setBusy(null); }
  };
  const ratesValue = (): Rates | null => { const out = {} as Rates; for (const a of APPS) { const n = Number(rates[a]); if (!rates[a].trim() || !Number.isFinite(n) || n < 0) return null; out[a] = n; } return out; };
  const pricesValue = (): Prices | null => {
    const out: Prices = {};
    for (const p of prices) { const i = Number(p.input); const o = Number(p.output); if (!p.model.trim() || !Number.isFinite(i) || !Number.isFinite(o) || i < 0 || o < 0) return null; out[p.model.trim()] = { input: i, output: o }; }
    return out;
  };
  const mark = (k: string) => setDirty((x) => ({ ...x, [k]: true }));

  const deploy: KvRow[] = Object.entries(s?.deployTime ?? {}).map(([k, v]) => ({
    k, mono: true, kSub: DEPLOY_NOTE[k],
    v: Array.isArray(v) ? (v.length ? { node: <div className="mono" style={{ fontSize: 12 }}>{v.map((x) => <div key={x}>{x}</div>)}</div> } : { t: 'none', fg: 'var(--ink-45)' }) : v == null ? { t: 'not set', fg: 'var(--ink-45)' } : { t: v, mono: true },
  }));
  const secrets: KvRow[] = (s?.secrets ?? []).map((x) => {
    const test = TEST_SECRETS.has(x.name);
    const pill = test && prod ? (x.present ? { label: 'SET', tone: 'warn' as const } : { label: 'ABSENT', tone: 'ok' as const }) : x.present ? { label: 'SET', tone: 'ok' as const } : { label: 'ABSENT', tone: test ? 'mute' as const : 'warn' as const };
    return { k: x.name, mono: true, kSub: test ? (prod ? 'expected absent on production' : 'test harness · expected on E2E and local') : undefined, v: { pill } };
  });
  const err = describeErr(d.error) ? { ...describeErr(d.error)!, retry: d.reload } : null;

  return (
    <>
      <Head title="Settings" sub="Runtime settings apply immediately. Deploy-time values change only with a release. Every change is logged with before and after." />
      <Grid>
        <Cell span={12}>
          <Block title="Runtime" sub="Stored in a database table the API reads. Applies immediately." asOf={hhmmss(d.at)} loading={d.loading && !s} error={err} note={mobile ? 'Editing is desktop-only.' : undefined}>
            {s && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {(s.settings ?? []).filter((x) => !OWN_EDITOR.has(x.key) && x.kind !== 'json').map((x) => (
                  <Declared key={x.key} s={x} mobile={mobile} busy={busy === x.key} onSave={(v) => void save(x.key, v, `${x.key}: ${show(v)}`)} />
                ))}
                <Group title="Translation provider override" sub="Applies to translations made from now on" stamp={s.runtime.translation_provider} dirty={!!dirty.translation_provider} busy={busy === 'translation_provider'} onSave={() => void save('translation_provider', provider, `Translation override: ${provider}`)} saveLabel="Apply">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Seg<Provider> value={provider} options={[{ value: 'auto', label: 'auto' }, { value: 'google', label: 'google' }, { value: 'openai', label: 'openai' }]} onChange={(v) => { if (mobile) return; setProvider(v); mark('translation_provider'); }} mono />
                    <span style={{ fontSize: 12, fontWeight: 300, color: 'var(--ink-62)' }}>{provider === 'auto' ? 'Each translation tries OpenAI and falls back to Google for that one request.' : `Every translation goes to ${provider === 'google' ? 'Google' : 'OpenAI'}.`}{s.deployTime.TRANSLATION_PROVIDER ? ` Deploy-time default: ${String(s.deployTime.TRANSLATION_PROVIDER)}.` : ''}</span>
                  </div>
                </Group>
                <Group title="Cost rates per app" sub="$ per hour · used for run cost, cost today and analytics; machine size is fixed per app" stamp={s.runtime.cost_rates} dirty={!!dirty.cost_rates && ratesValue() !== null} busy={busy === 'cost_rates'} onSave={() => { const v = ratesValue(); if (v) void save('cost_rates', v, 'Cost rates saved'); }} saveLabel="Save rates">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                    {APPS.map((a) => <Field key={a} label={`${APP_LABEL[a]} · $/h`} type="number" mono value={rates[a]} onChange={(v) => { if (mobile) return; setRates((r) => ({ ...r, [a]: v })); mark('cost_rates'); }} />)}
                  </div>
                </Group>
                <Group title="LLM price table" sub="$ per 1M tokens · summary cost in Analytics is tokens × these prices; the model itself is SUMMARY_MODEL, set by a release" stamp={s.runtime.llm_prices} dirty={!!dirty.llm_prices && pricesValue() !== null} busy={busy === 'llm_prices'} onSave={() => { const v = pricesValue(); if (v) void save('llm_prices', v, 'LLM prices saved'); }} saveLabel="Save prices">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {prices.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink-45)' }}>No prices yet{s.deployTime.SUMMARY_MODEL ? ` · the summary model is ${String(s.deployTime.SUMMARY_MODEL)}` : ''}.</span>}
                    {prices.map((p, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px,2fr) minmax(100px,1fr) minmax(100px,1fr) auto', gap: 10, alignItems: 'end' }}>
                        <Field label="Model" mono value={p.model} onChange={(v) => { if (mobile) return; setPrices((x) => x.map((y, j) => (j === i ? { ...y, model: v } : y))); mark('llm_prices'); }} />
                        <Field label="Input $/1M" type="number" mono value={p.input} onChange={(v) => { if (mobile) return; setPrices((x) => x.map((y, j) => (j === i ? { ...y, input: v } : y))); mark('llm_prices'); }} />
                        <Field label="Output $/1M" type="number" mono value={p.output} onChange={(v) => { if (mobile) return; setPrices((x) => x.map((y, j) => (j === i ? { ...y, output: v } : y))); mark('llm_prices'); }} />
                        <button type="button" className="btn btn--sm btn--danger" style={{ marginBottom: 1 }} disabled={mobile} onClick={() => { setPrices((x) => x.filter((_, j) => j !== i)); mark('llm_prices'); }}>Remove</button>
                      </div>
                    ))}
                    <div><button type="button" className="btn btn--sm" disabled={mobile} onClick={() => { setPrices((x) => [...x, { model: x.length === 0 && s.deployTime.SUMMARY_MODEL ? String(s.deployTime.SUMMARY_MODEL) : '', input: '', output: '' }]); mark('llm_prices'); }}>Add model</button></div>
                  </div>
                </Group>
              </div>
            )}
          </Block>
        </Cell>
        <Cell span={6}>
          <Block title="Deploy-time" sub="Read-only. Changed by a release." asOf={hhmmss(d.at)} loading={d.loading && !s} error={err} empty={s && deploy.length === 0 ? 'The API reported no deploy-time values.' : undefined}>
            <Kv rows={deploy} kw="46%" />
          </Block>
        </Cell>
        <Cell span={6}>
          <Block title="Secrets" sub="Names and presence only. Values are never shown. Changed by a release." asOf={hhmmss(d.at)} loading={d.loading && !s} error={err}>
            <Kv rows={secrets} kw="58%" />
            {s && <div style={{ padding: '10px 14px', fontSize: 11.5, fontWeight: 300, color: 'var(--ink-55)' }}>
              <Pill tone="ok">ABSENT</Pill> on AUTH_DEV_SECRET and RUN_SECRET is the expected state on production; <Pill tone="warn">SET</Pill> there means the test harness secrets leaked into the release.
            </div>}
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 6}>
          <Block title="External services" sub="What the API app can reach. Presence only, never values." asOf={hhmmss(ov.at)} loading={ov.loading && !cfg} error={describeErr(ov.error) && !cfg ? { ...describeErr(ov.error)!, retry: ov.reload } : null}>
            {cfg && <Kv kw="40%" rows={[
              { k: 'Translation', v: fallbackCell({ fr, override: cfg.translationProvider ?? '—' }) },
              { k: 'OpenAI', v: set(cfg.openai) },
              { k: 'WorkOS', v: set(cfg.workos) },
              { k: 'Push · APNs', v: set(cfg.apns) },
              { k: 'Push · FCM', v: set(cfg.fcm) },
              { k: 'Fly token', v: set(cfg.fly) },
              { k: 'GitHub dispatch', v: set(cfg.github) },
            ]} />}
          </Block>
        </Cell>
      </Grid>
    </>
  );
}
